import { useLayoutEffect } from "react";

import { filter, includes, sortBy } from "lodash/fp";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

interface UseBannersProps {
  // id of the banner, must be unique or an error is thrown
  id: string;
  // lower numbers will have higher priority. if more than one banner has the same priority
  // we will show at most two of the banners with the same priority
  priority: number;
  // if false, will not show the banner regardless of priority
  shouldShow: boolean;
}

interface BannerState {
  registry: Record<string, UseBannersProps>;
  activeBanners: string[];
  registerBanner: (banner: UseBannersProps) => void;
  unregisterBanner: (id: string) => void;
}

// Get active banners based on priority and whether they should show.
// If all priority values are unique only show max one banner, else if
// there are multiple banners that should be displayed with the same priority,
// show two
const getActiveBanners = (
  registry: Record<string, UseBannersProps>
): string[] => {
  const activeBanners = filter(
    { shouldShow: true },
    sortBy("priority", Object.values(registry))
  );

  if (activeBanners.length > 1) {
    const [first, second] = activeBanners;
    if (first.priority === second.priority) {
      return [first.id, second.id];
    }
  }

  return activeBanners.length ? [activeBanners[0].id] : [];
};

const useBannerStore = createWithEqualityFn<BannerState>(
  (set) => ({
    // register is an object with type map string to banner
    registry: {},
    activeBanners: [],
    registerBanner: (banner: UseBannersProps) =>
      set((state) => {
        const newRegistry = { ...state.registry, [banner.id]: banner };

        return {
          registry: newRegistry,
          activeBanners: getActiveBanners(newRegistry),
        };
      }),
    unregisterBanner: (id: string) =>
      set((state) => {
        if (!state.registry[id]) {
          return state;
        }
        const { [id]: _, ...newRegistry } = state.registry;
        return {
          registry: newRegistry,
          activeBanners: getActiveBanners(newRegistry),
        };
      }),
  }),
  shallow
);

const useBanners = (banner: UseBannersProps) => {
  const { registerBanner, unregisterBanner, activeBanners } = useBannerStore(
    (state) => ({
      registerBanner: state.registerBanner,
      unregisterBanner: state.unregisterBanner,
      activeBanners: state.activeBanners,
    })
  );

  useLayoutEffect(() => {
    registerBanner(banner);

    return () => {
      unregisterBanner(banner.id);
    };
  }, [banner.id, banner.priority, banner.shouldShow]);

  return {
    show: includes(banner.id, activeBanners),
  };
};

export default useBanners;
